<script setup lang="ts">
import { useConvexQuery } from 'convex-vue';
import { computed } from 'vue';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import { useInjectGameEngine } from '@/composables/useGameEngine';
import { useSubmitPieceHighScore } from '@/composables/useSubmitPieceHighScore';
import { getGradeForAccuracy } from '@/utils/grade';
import GradeBadge from './GradeBadge.vue';

const props = defineProps<{
  musicalPieceId: Id<'musicalPieces'>;
}>();

const { data: highScore } = useConvexQuery(
  api.pieceHighScores.getForPiece,
  computed(() => ({ musicalPieceId: props.musicalPieceId })),
);

const highScoreGrade = computed(() =>
  highScore.value
    ? getGradeForAccuracy(highScore.value.accuracy, highScore.value.targetLineHidden)
    : null,
);

// Fire once on mount. The parent renders this row only after the run has
// reached its end (gated by progress >= 100); a "Play Again" round unmounts
// the parent dialog, so the next end-of-game gets a fresh instance.
useSubmitPieceHighScore(useInjectGameEngine())(props.musicalPieceId);
</script>

<template>
  <div class="flex justify-between items-center pt-2 mt-2 border-t border-white/10">
    <span class="text-text-secondary">Highscore</span>
    <div class="flex items-center gap-2">
      <GradeBadge :grade="highScoreGrade" size="sm" />
      <span v-if="highScore" class="text-text-primary font-bold">{{ highScore.score }}</span>
      <span v-else class="text-text-secondary/60 text-sm">No record yet</span>
    </div>
  </div>
</template>
